<template>
  <div
    v-style="gradient"
    class="w-full absolute"
    :class="gradientPositionClasses"
  />
</template>

<script setup lang="ts">
import type { Responsive } from '#types/common'
import type { GradientStyle } from '#types/styles'

type GradientPosition = 'top' | 'bottom' | 'center'
type GradientAlignment = 'left' | 'center' | 'right' | undefined

const {
  gradientTone = 'transparent',
  gradientBreakpoints,
  gradientStyle,
  ...props
} = defineProps<{
  position: GradientPosition | Responsive<GradientPosition>
  alignment?: GradientAlignment | Responsive<GradientAlignment>
} & GradientStyle>()

const { breakpoints } = useAppConfig().ds

const position = isObject(props.position) ? props.position : { sm: props.position }
const alignment = isObject(props.alignment) ? props.alignment : { sm: props.alignment }

const gradientStyles = { full: 35, focused: 50 }

const getGradientByPositionAndAlignment = (pos?: GradientPosition, ali?: GradientAlignment) => {
  return `linear-gradient(to ${pos === 'center' ? (ali === 'right' ? 'right' : 'left') : pos}, transparent, ${gradientTone || 'transparent'})`
}

const gradientPosition = {
  top: { sm: '<md:top-0', md: 'md:top-0' },
  bottom: { sm: '<md:bottom-0', md: 'md:bottom-0' },
  center: { sm: '<md:top-1/2 <md:-translate-y-1/2', md: 'md:top-1/2 md:-translate-y-1/2' },
}

const smallPosition = getValueForBreakpoint('sm', position)
const mediumPosition = getValueForBreakpoint('md', position)

const gradientPositionClasses = [
  smallPosition && gradientPosition[smallPosition].sm,
  mediumPosition && gradientPosition[mediumPosition].md
]

const gradient = {
  display: Object.entries(gradientBreakpoints!).reduce((acc, [key, value]) => ({
    ...acc,
    [key]: value ? 'block' : 'none'
  }), {} as Responsive),
  bgv: Object.keys(breakpoints).reduce((acc, key) => ({
    ...acc,
    [key]: getGradientByPositionAndAlignment(
      getValueForBreakpoint(key as keyof Responsive, position),
      getValueForBreakpoint(key as keyof Responsive, alignment)
    )
  }), {} as Responsive),
  h: `${gradientStyles[gradientStyle!] || 0}%`,
}
</script>
